import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { v4 } from 'uuid';

import {
  IPayoutVerificationChallenge,
  IWalletCashOut,
  IWalletCashOutBrand,
  IWalletCashOutDetail,
  IWalletCashOutOptions,
  IWalletCashOutQuote,
  WalletCashOutStatus,
} from '@share/common-types';
import {
  createWalletCashOutAsync,
  getWalletCashOutAsync,
  getWalletCashOutErrorMessage,
  getWalletCashOutOptionsAsync,
  getWalletCashOutsAsync,
  quoteWalletCashOutAsync,
  sendWalletCashOutVerificationAsync,
} from '@share/api';
import { getUserWallet } from '@share/store/slices';
import { GetWalletAmount, RootState, Toaster } from '@share/utils';
import { ScrollBehaviorEnum } from '@common-types';

import { EXTERNAL_REDIRECT_DELAY_MS, openExternalWithNotice } from './open-external';

export type Step = 'form' | 'confirm' | 'verify' | 'result';

export const TERMINAL_STATUSES: WalletCashOutStatus[] = ['Delivered', 'DeliveryFailed', 'Failed', 'Canceled', 'Reversed'];
export const FAILED_STATUSES: WalletCashOutStatus[] = ['Failed', 'Canceled', 'Reversed'];
export const CODE_LENGTH = 6;
const QUICK_AMOUNTS = [25, 50, 100, 250];

// The stages shown above the form; the code step belongs to the review.
export const STEP_INDEX: Record<Step, number> = { form: 0, confirm: 1, verify: 1, result: 2 };
export const STEP_LABELS = ['wallet.cashout.step.amount', 'wallet.cashout.step.review', 'wallet.cashout.step.done'];
const DEFAULT_RESEND_SECONDS = 60;

const toCents = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
const floorToCents = (value: number) => Math.floor(value * 100 + 1e-9) / 100;

const hasFee = (options: IWalletCashOutOptions) => !!options.feeAmount || !!options.feePercentage;

/** The fee Payments charges on an amount: fixed plus percentage, rounded to cents (WalletCashOutFeeCalculator). */
export const cashOutFee = (options: IWalletCashOutOptions, amount: number) =>
  toCents((options.feeAmount ?? 0) + (amount * (options.feePercentage ?? 0)) / 100);

/** What leaves the balance: in Gross the fee is added on top of the amount, in Net it comes out of the gift card. */
export const cashOutDebit = (options: IWalletCashOutOptions, amount: number) =>
  options.feeMode === 'Gross' ? toCents(amount + cashOutFee(options, amount)) : amount;

/**
 * The largest amount the member can request: within the maximum and, in Gross, small enough that the
 * amount plus its fee still fits in the cashable balance.
 */
export const maxCashOutAmount = (options: IWalletCashOutOptions) => {
  const ceiling = floorToCents(Math.min(options.cashableBalance, options.maxAmount ?? Number.POSITIVE_INFINITY));
  if (options.feeMode !== 'Gross' || !hasFee(options)) {
    return Math.max(ceiling, 0);
  }
  const fits = (value: number) => value <= ceiling && cashOutDebit(options, value) <= options.cashableBalance;
  const percentage = (options.feePercentage ?? 0) / 100;
  let max = Math.min(ceiling, floorToCents((options.cashableBalance - (options.feeAmount ?? 0)) / (1 + percentage)));
  // The fee is rounded to cents, so the estimate can be a cent off either way: settle on the exact one.
  while (max > 0 && !fits(max)) {
    max = toCents(max - 0.01);
  }
  while (fits(toCents(max + 0.01))) {
    max = toCents(max + 0.01);
  }
  return Math.max(max, 0);
};

const fitsBrand = (brand: IWalletCashOutBrand, amount: number) =>
  (brand.minAmount == null || amount >= brand.minAmount) &&
  (brand.maxAmount == null || amount <= brand.maxAmount);

/**
 * Stands in when the options cannot be read (an empty answer or a failed request): the member is
 * shown as not eligible, but the page still renders, so their previous cash-outs and redemption
 * links stay reachable.
 */
const NOT_AVAILABLE_OPTIONS: IWalletCashOutOptions = {
  eligible: false,
  reason: null,
  cashableBalance: 0,
  commissionBalance: 0,
  requiresVerification: false,
  feeMode: 'Net',
  currencyCode: 'USD',
  brands: [],
};

// The options can come back without a catalog (e.g. a wallet that can't be cashed out); every
// reader expects a list, so an absent one becomes empty here, once.
const normalizeOptions = (data: IWalletCashOutOptions | null | undefined | ''): IWalletCashOutOptions =>
  (data && typeof data === 'object' ? { ...data, brands: data.brands ?? [] } : NOT_AVAILABLE_OPTIONS);

// The options and the history load independently: options that cannot be read must not hide the
// history, which is where the member re-opens gift cards they already paid for.
const loadOptions = (walletCode: string) =>
  getWalletCashOutOptionsAsync(walletCode)
    .then((response) => normalizeOptions(response.data))
    .catch(() => NOT_AVAILABLE_OPTIONS);

/**
 * The whole wallet cash-out flow (options, quote, emailed code, create, status, history) without
 * any UI, so the cash-out page and the My Account hub render the same flow their own way.
 */
export function useWalletCashOut() {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const { account, userWallet, userWalletData } = useSelector((state: RootState) => state.loginStore);
  const walletCode = userWalletData?.walletCode;

  const clientCashName = isEmpty(account?.walletClientCashName)
    ? 'Client Cash'
    : account?.walletClientCashName;

  const [isLoading, setIsLoading] = useState(true);
  const [options, setOptions] = useState<IWalletCashOutOptions | null>(null);
  const [history, setHistory] = useState<IWalletCashOut[]>([]);
  const [step, setStep] = useState<Step>('form');
  const [amount, setAmount] = useState<number | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [quote, setQuote] = useState<IWalletCashOutQuote | null>(null);
  const [detail, setDetail] = useState<IWalletCashOutDetail | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [challenge, setChallenge] = useState<IPayoutVerificationChallenge | null>(null);
  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState(0);

  // One key per confirmation: a retry after a network error resends the same key, so it cannot
  // create a second cash-out. A new confirmation gets a new key.
  const idempotencyKey = useRef<string | null>(null);

  // The result sits above the history, so opening a cash-out from the history brings it into view.
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (step === 'result' && detail) {
      resultRef.current?.scrollIntoView({ behavior: ScrollBehaviorEnum.Smooth });
    }
  }, [step, detail]);

  useEffect(() => {
    if (resendIn <= 0) {
      return;
    }
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const formatPoints = useCallback(
    (value: number) => GetWalletAmount(value, account, userWalletData, clientCashName),
    [account, userWalletData, clientCashName],
  );

  const formatMoney = useCallback(
    (value: number, currency: string) =>
      intl.formatNumber(value, { style: 'currency', currency: currency || 'USD' }),
    [intl],
  );

  const errorMessage = useCallback(
    (error: any) =>
      getWalletCashOutErrorMessage(error, intl.formatMessage({ id: 'wallet.cashout.error.generic' })),
    [intl],
  );

  // After a cash-out both change: the balances in the options and the list.
  const reloadAfterCashOut = useCallback(async () => {
    if (!walletCode) {
      return;
    }

    const [loadedOptions, historyResponse] = await Promise.all([
      loadOptions(walletCode),
      getWalletCashOutsAsync(walletCode),
    ]);
    setOptions(loadedOptions);
    setHistory(Array.isArray(historyResponse.data) ? historyResponse.data : []);
  }, [walletCode]);

  useEffect(() => {
    if (!walletCode) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    Promise.all([loadOptions(walletCode), getWalletCashOutsAsync(walletCode)])
      .then(([loadedOptions, historyResponse]) => {
        if (!isCancelled) {
          setOptions(loadedOptions);
          setHistory(Array.isArray(historyResponse.data) ? historyResponse.data : []);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setOptions(NOT_AVAILABLE_OPTIONS);
          Toaster.error(errorMessage(error));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [walletCode, errorMessage]);

  const brandsForAmount = useMemo(
    () => (options?.brands ?? []).filter((brand) => amount == null || fitsBrand(brand, amount)),
    [options, amount],
  );

  const selectedBrand = options?.brands.find((brand) => brand.productId === productId);

  const selectAmount = (value: number | null) => {
    setAmount(value);
    setProductId(null);
  };

  const amountError = useMemo(() => {
    if (amount == null || !options) {
      return null;
    }
    if (options.minAmount != null && amount < options.minAmount) {
      return intl.formatMessage({ id: 'wallet.cashout.amount.min' }, { min: formatPoints(options.minAmount) });
    }
    if (options.maxAmount != null && amount > options.maxAmount) {
      return intl.formatMessage({ id: 'wallet.cashout.amount.max' }, { max: formatPoints(options.maxAmount) });
    }
    if (amount > options.cashableBalance) {
      return intl.formatMessage({ id: 'wallet.cashout.amount.over_cashable' });
    }
    if (!hasFee(options)) {
      return null;
    }
    const fee = cashOutFee(options, amount);
    if (options.feeMode === 'Gross' && cashOutDebit(options, amount) > options.cashableBalance) {
      return intl.formatMessage(
        { id: 'wallet.cashout.amount.over_cashable_fee' },
        {
          fee: formatMoney(fee, options.currencyCode),
          debit: formatMoney(cashOutDebit(options, amount), options.currencyCode),
          max: formatMoney(maxCashOutAmount(options), options.currencyCode),
        },
      );
    }
    if (options.feeMode !== 'Gross' && fee >= amount) {
      return intl.formatMessage({ id: 'wallet.cashout.amount.fee_too_high' }, { fee: formatMoney(fee, options.currencyCode) });
    }
    return null;
  }, [amount, options, intl, formatPoints, formatMoney]);

  // The profile's fee in words ("$2.00 + 3%"), for the hint under the amount.
  const feeHint = useMemo(() => {
    if (!options || !hasFee(options)) {
      return intl.formatMessage({ id: 'wallet.cashout.fee.none' });
    }
    const parts = [
      options.feeAmount ? formatMoney(options.feeAmount, options.currencyCode) : null,
      options.feePercentage ? `${intl.formatNumber(options.feePercentage, { maximumFractionDigits: 2 })}%` : null,
    ].filter(Boolean);
    return intl.formatMessage(
      { id: options.feeMode === 'Gross' ? 'wallet.cashout.fee.gross_amount' : 'wallet.cashout.fee.net_amount' },
      { fee: parts.join(' + ') },
    );
  }, [options, intl, formatMoney]);

  // What the typed or picked amount costs, before the quote: the fee and what it means for the member.
  const amountBreakdown = useMemo(() => {
    if (amount == null || !options || amountError || !hasFee(options)) {
      return null;
    }
    const fee = cashOutFee(options, amount);
    return options.feeMode === 'Gross'
      ? intl.formatMessage(
        { id: 'wallet.cashout.amount.breakdown.gross' },
        { fee: formatMoney(fee, options.currencyCode), debit: formatMoney(cashOutDebit(options, amount), options.currencyCode) },
      )
      : intl.formatMessage(
        { id: 'wallet.cashout.amount.breakdown.net' },
        { fee: formatMoney(fee, options.currencyCode), payout: formatMoney(toCents(amount - fee), options.currencyCode) },
      );
  }, [amount, options, amountError, intl, formatMoney]);

  const openDetail = useCallback(
    async (cashOutCode: string) => {
      if (!walletCode) {
        return;
      }

      setIsBusy(true);
      try {
        const response = await getWalletCashOutAsync(walletCode, cashOutCode);
        setDetail(response.data);
        setStep('result');
      } catch (error) {
        Toaster.error(errorMessage(error));
      } finally {
        setIsBusy(false);
      }
    },
    [walletCode, errorMessage],
  );

  const handleContinue = async () => {
    if (!walletCode || amount == null || !productId || amountError) {
      return;
    }

    setIsBusy(true);
    try {
      const response = await quoteWalletCashOutAsync(walletCode, amount, productId);
      if (!response.data?.success || !response.data.quote) {
        Toaster.error(response.data?.failureReason ?? intl.formatMessage({ id: 'wallet.cashout.error.generic' }));
        return;
      }

      setQuote(response.data.quote);
      idempotencyKey.current = v4();
      setStep('confirm');
    } catch (error) {
      Toaster.error(errorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  // While the wallet holds commissions, the cash-out is confirmed with a code emailed to the member.
  const sendCode = async () => {
    setIsBusy(true);
    try {
      const response = await sendWalletCashOutVerificationAsync();
      setChallenge(response.data);
      setCode('');
      setResendIn(response.data?.resendAfterSeconds || DEFAULT_RESEND_SECONDS);
      setStep('verify');
    } catch (error) {
      Toaster.error(errorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!walletCode || !quote || !productId || !idempotencyKey.current) {
      return;
    }

    if (options?.requiresVerification && (!challenge || code.length !== CODE_LENGTH)) {
      return;
    }

    setIsBusy(true);
    try {
      const response = await createWalletCashOutAsync(
        walletCode,
        {
          amount: quote.requestedAmount,
          productId,
          verificationId: challenge?.verificationId,
          verificationCode: challenge ? code : undefined,
        },
        idempotencyKey.current,
      );

      if (response.data?.payoutCode) {
        await openDetail(response.data.payoutCode);
        // The header balance and the commissions view read the stored wallet.
        if (userWallet) {
          dispatch(getUserWallet(userWallet));
        }
      } else {
        Toaster.error(response.data?.failureReason ?? intl.formatMessage({ id: 'wallet.cashout.error.generic' }));
      }

      // The cash-out exists either way; a failed refresh only means the new numbers show up later.
      reloadAfterCashOut().catch((error) => Toaster.error(errorMessage(error)));
    } catch (error) {
      Toaster.error(errorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  // The redemption link leaves the site: the member is told so before going.
  const handleRedeem = (link: string) => {
    const copy = {
      title: intl.formatMessage({ id: 'wallet.cashout.redirect.title' }),
      hint: intl.formatMessage({ id: 'wallet.cashout.redirect.hint' }),
    };

    if (openExternalWithNotice(link, copy)) {
      return;
    }

    // The new tab was blocked: same notice over this page, then leave in this tab.
    setIsRedirecting(true);
    window.setTimeout(() => window.location.assign(link), EXTERNAL_REDIRECT_DELAY_MS);
  };

  const handleStartOver = () => {
    setStep('form');
    setAmount(null);
    setProductId(null);
    setQuote(null);
    setDetail(null);
    setChallenge(null);
    setCode('');
    idempotencyKey.current = null;
  };

  // Quick amounts the member can actually cash out: within the profile's bounds and the cashable
  // balance, fee included. "All" is the largest of them (see maxCashOutAmount).
  const quickAmounts = useMemo(() => {
    if (!options?.eligible) {
      return [];
    }
    const all = maxCashOutAmount(options);
    const fits = (value: number) =>
      value > 0 &&
      value <= all &&
      (options.minAmount == null || value >= options.minAmount) &&
      (options.feeMode === 'Gross' || cashOutFee(options, value) < value);
    const fixed = QUICK_AMOUNTS.filter((value) => fits(value) && value < all).map((value) => ({ value, isAll: false }));
    return fits(all) ? [...fixed, { value: all, isAll: true }] : fixed;
  }, [options]);

  return {
    intl,
    account,
    userWalletData,
    walletCode,
    clientCashName,
    isLoading,
    options,
    history,
    step,
    setStep,
    amount,
    productId,
    setProductId,
    quote,
    detail,
    isBusy,
    isRedirecting,
    challenge,
    code,
    setCode,
    resendIn,
    resultRef,
    formatPoints,
    formatMoney,
    brandsForAmount,
    selectedBrand,
    selectAmount,
    amountError,
    feeHint,
    amountBreakdown,
    openDetail,
    handleContinue,
    sendCode,
    handleConfirm,
    handleRedeem,
    handleStartOver,
    quickAmounts,
  };
}
